import { event } from "@foretagsplatsen/yaem";
import ArrayStream from "./streams/ArrayStream.js";

/**
 * This class manages a stream of items, and provides helpful methods to manipulate that stream.
 *
 * Before the stream is asked for new data, the onDataFetch event is
 * triggered with {loading: true}. When data has been fetched, the
 * onDataFetch event is triggered with {items: [...], loading: false}.
 *
 * @param {AbstractStream} stream - An input stream providing the elements to use
 * @param {string} [searchString] - When defined, specify a text that
 *   items should match to be returned
 * @param {string} [sortFieldAccessor] - Function or string used to get an item property
 * @param {boolean} [sortAscending] - When true, the sorting is ascending, otherwise the sorting is descending
 * @param {string} [groupByFieldAccessor] - When defined, specify
 *   which accessor of items should be used to group them
 * @param {number} [pageIndex] - Index of the current page to show
 * @param {number} [pageSize] - Number of items per page
 */
export default class ListSource {
	constructor({
		stream = new ArrayStream(),
		searchString,
		sortFieldAccessor,
		sortAscending = true,
		groupByFieldAccessor,
		pageIndex = 0,
		pageSize,
	} = {}) {
		this._stream = stream;
		this._newItemsPromise = undefined;

		this._onDataFetch = event();

		this._searchString = searchString;

		this._sortFieldAccessor = sortFieldAccessor;
		this._sortAscending = sortAscending;

		this._groupByFieldAccessor = groupByFieldAccessor;

		this._pageSize = pageSize;
		this._pageIndex = pageIndex;
	}

	goTo({ pageIndex }) {
		this._pageIndex = pageIndex;
		return this.fetch();
	}

	registerOnDataFetch(fn) {
		if (this._newItemsPromise?.resolved) {
			this._newItemsPromise.then(({ items, unpaginatedSize }) =>
				fn({ items, unpaginatedSize, loading: false }),
			);
		}

		return this._onDataFetch.register(fn);
	}

	/**
	 * Fetch the new data using a Promise.
	 *
	 * @abstract
	 * @return {Promise<object[]>}
	 */
	async fetch() {
		this._onDataFetch.trigger({ loading: true });

		this._newItemsPromise = this._stream.fetch(this._makeFetchOptions());

		const { items, unpaginatedSize } = await this._newItemsPromise;

		// If the ListSource was transformed while the stream was
		// fetching data, it is possible that
		// this._newItemsPromise=undefined
		if (!this._newItemsPromise) {
			return { cancelled: true };
		}

		this._newItemsPromise.resolved = true;

		this._onDataFetch.trigger({ items, unpaginatedSize, loading: false });

		return { items, cancelled: false, unpaginatedSize };
	}

	orderBy(propertyName) {
		if (this.isCurrentSortCriterion(propertyName)) {
			this._sortAscending = !this._sortAscending;
		} else {
			this._sortAscending = true;
			this._sortFieldAccessor = propertyName;
		}

		this._cancelCurrentFetch();
	}

	applySearchFrom(searchFieldWidget) {
		searchFieldWidget.applySearchOnListSource(this);
	}

	searchBy(searchString) {
		this._searchString = searchString;
		this._pageIndex = 0;
		this._cancelCurrentFetch();
	}

	getAscendingSort() {
		return this._sortAscending;
	}

	isCurrentSortCriterion(field) {
		return this._sortFieldAccessor === field;
	}

	getPageSize() {
		return this._pageSize;
	}

	getPageIndex() {
		return this._pageIndex;
	}

	_makeFetchOptions() {
		return {
			searchingOptions: {
				searchString: this._searchString,
			},
			sortingOptions: {
				propertyName: this._sortFieldAccessor,
				isAscendingOrder: this._sortAscending,
			},
			groupingOptions: {
				propertyName: this._groupByFieldAccessor,
			},
			paginatingOptions: {
				numberOfItemsToFetch: this._pageSize,
				numberOfItemsToSkip: this._pageSize * this._pageIndex,
			},
		};
	}

	_cancelCurrentFetch() {
		this._newItemsPromise = undefined;
	}

	static makeAndFetch(...args) {
		const listSource = new this(...args);

		listSource.fetch();

		return listSource;
	}
}
